import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

const STORAGE_KEY = 'flote-age-verified';
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

interface AgeVerificationState {
  verified: boolean;
  timestamp: number;
}

interface AgeVerificationContextValue {
  isVerified: boolean;
  isChecking: boolean;
  verify: () => void;
}

const AgeVerificationContext = createContext<AgeVerificationContextValue | undefined>(undefined);

function getStoredState(): AgeVerificationState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as AgeVerificationState;
  } catch {
    return null;
  }
}

function isStateValid(state: AgeVerificationState | null): boolean {
  if (!state || !state.verified) return false;
  return Date.now() - state.timestamp < TWENTY_FOUR_HOURS_MS;
}

export function AgeVerificationProvider({ children }: { children: ReactNode }) {
  const [isChecking, setIsChecking] = useState(true);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    const stored = getStoredState();
    setIsVerified(isStateValid(stored));
    setIsChecking(false);
  }, []);

  const verify = useCallback(() => {
    const state: AgeVerificationState = {
      verified: true,
      timestamp: Date.now(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // localStorage may be unavailable, continue anyway
    }
    setIsVerified(true);
  }, []);

  return (
    <AgeVerificationContext.Provider value={{ isVerified, isChecking, verify }}>
      {children}
    </AgeVerificationContext.Provider>
  );
}

export function useAgeVerification(): AgeVerificationContextValue {
  const context = useContext(AgeVerificationContext);
  if (context === undefined) {
    throw new Error('useAgeVerification must be used within an AgeVerificationProvider');
  }
  return context;
}
